import type { FormField } from "@tellescope/types-models";
import { advanceWhile, collectFieldPath, MAX_FIELD_PATH_DEPTH, type FieldPathWalkContext } from "./walk-field-path";
import { convertFormFieldType } from "./convert-form-field-type";
import { getNextField } from "./get-next-field";
import { isAnsweredQuestionGroup, isPassThroughField, occupiesOwnPage, subFieldIdsOf } from "./question-group";
import type { FormFieldNode } from "./types";

export interface InitialNavState {
  /** The field the wizard should open on */
  activeField: FormFieldNode;
  /** Ids treated as already navigated — the fields walked past, plus any sub-fields they carry */
  included: Set<string>;
  /** Back-navigation stack leading to `activeField`, oldest first */
  prevStack: FormFieldNode[];
}

export interface ResolveInitialNavStateArgs {
  /** Root of the field graph */
  fieldTree: FormFieldNode;
  /** All fields by id, used to resolve Question Group sub-field types */
  fieldsMap: Record<string, FormField | undefined>;
  /** Ids belonging to a Question Group, excluded from `prevStack` since they share their parent's page */
  subFieldIds: ReadonlySet<string>;
  /** Ids answered this session */
  answeredFieldIds: ReadonlySet<string>;
  /** Ids answered by a previously saved response */
  existingResponseFieldIds: ReadonlySet<string>;
  /**
   * Field to resume at. Fields before it are walked past regardless of whether
   * they were answered; when omitted, the walk is "already past" from the root.
   */
  startingFieldId?: string;
  walkContext: FieldPathWalkContext;
}

/**
 * Picks the field a returning enduser should land on, skipping what they have already done.
 *
 * The walk steps forward from the root while a field is skippable, where skippable
 * means any of: already answered (directly, or every sub-field of a Question Group),
 * pass-through (renders no UI), display-only with an answer later in the path, or
 * positioned before `startingFieldId`. It comes to rest on the first field that is
 * none of those.
 *
 * Display-only fields (descriptions and the like) are only skipped when something
 * *after* them was answered — otherwise a form opening on an intro screen would
 * jump straight past it. Pass-through fields are excluded from `prevStack` so that
 * going back from the resolved field lands on a real, rendered question rather than
 * an invisible one.
 *
 * The result is applied once on mount; it is not a live projection of the answers.
 */
export const resolveInitialNavState = ({
  fieldTree,
  fieldsMap,
  subFieldIds,
  answeredFieldIds,
  existingResponseFieldIds,
  startingFieldId,
  walkContext,
}: ResolveInitialNavStateArgs): InitialNavState => {
  const wasPreviouslySaved = (field: FormField) =>
    answeredFieldIds.has(field.id) ||
    existingResponseFieldIds.has(field.id) ||
    isAnsweredQuestionGroup(field, answeredFieldIds, existingResponseFieldIds);

  const path = collectFieldPath(fieldTree, walkContext);
  const hasResponseAfter = path.reduceRight<{ map: Record<string, boolean>; seenResponse: boolean }>(
    (acc, node) => ({
      map: { ...acc.map, [node.value.id]: acc.seenResponse },
      seenResponse: acc.seenResponse || wasPreviouslySaved(node.value),
    }),
    { map: {}, seenResponse: false }
  ).map;

  const skipVisited = (
    current: FormFieldNode,
    prevStack: FormFieldNode[],
    included: Set<string>,
    hasPassedStartingField: boolean,
    depth: number
  ): { activeField: FormFieldNode; prevStack: FormFieldNode[]; included: Set<string> } => {
    if (depth >= MAX_FIELD_PATH_DEPTH) return { activeField: current, prevStack, included };

    const isHiddenValue = isPassThroughField(current.value, fieldsMap);
    const passedStartingField =
      hasPassedStartingField ||
      current.value.id === startingFieldId ||
      prevStack.some((f) => f.value.id === startingFieldId);

    const skippableDisplayOnly =
      convertFormFieldType(current.value.type) === null && hasResponseAfter[current.value.id];
    if (!skippableDisplayOnly && !isHiddenValue && !wasPreviouslySaved(current.value) && passedStartingField) {
      return { activeField: current, prevStack, included };
    }

    const next = getNextField(
      current,
      walkContext.responsesByFieldId[current.value.id],
      walkContext.allResponses,
      walkContext.logicOptions
    );
    if (!next) return { activeField: current, prevStack, included };

    return skipVisited(
      next,
      occupiesOwnPage(current.value, subFieldIds, fieldsMap) ? [...prevStack, current] : prevStack,
      new Set([...included, ...subFieldIdsOf(current.value), next.value.id]),
      passedStartingField,
      depth + 1
    );
  };

  const visited = skipVisited(fieldTree, [], new Set([fieldTree.value.id]), !startingFieldId, 0);

  const settled = advanceWhile(visited.activeField, (node) => isPassThroughField(node.value, fieldsMap), walkContext);

  return {
    activeField: settled.node,
    included: new Set([...visited.included, settled.node.value.id]),
    prevStack: visited.prevStack,
  };
};
